// TODO: Move this out of Storyblok folder
// MARK: used in V2
import classNames from "classnames"
import { navigate } from "gatsby"
import React, { useContext, useEffect, useState } from "react"

import {
  useClearUnavailableProductsEffect,
  useGetCartQuantity,
  useRemoveBundle,
  useRemoveProduct,
  useUpdateBundleCartQuantity,
  useUpdateProductCartQuantity,
} from "../../../cart/hooks"
import { CartContext } from "../../../cart/store"
import {
  useClearPaidCart,
  useHandleSignupCheckout,
} from "../../../checkout/hooks"
import PillButton from "../../../components/elements/V2/Buttons/PillButton"
import TextButton from "../../../components/elements/V2/Buttons/TextButton"
import Typography from "../../../components/elements/V2/Typography"
import { Color } from "../../../constants/V2/color"
import { formatPrice } from "../../../utils/price"

import CartCard from "@components/elements/V2/CartCard"
import { isConsolidatedBundle } from "@utils/shop"
import { withPrefix } from "@utils/url"

type Props = {
  isLoading?: boolean
}

const CartPreviewModalContent = ({ isLoading }: Props) => {
  const cartContext = useContext(CartContext)
  useClearUnavailableProductsEffect()
  const updateProductCartQuantity = useUpdateProductCartQuantity()
  const updateBundleCartQuantity = useUpdateBundleCartQuantity()
  const removeProduct = useRemoveProduct()
  const removeBundle = useRemoveBundle()
  const {
    isLoading: isHandlingSignupCheckout,
    isSuccessful,
    handleLoginCheckout,
    handleRedirect,
  } = useHandleSignupCheckout()
  const numberOfItemsInCart = useGetCartQuantity()

  const cartItems: YocoCom.ConsolidatedCartItem[] = cartContext.products

  const checkPaidCart = useClearPaidCart()

  useEffect(() => {
    checkPaidCart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const cartTotal = formatPrice(
    cartItems.reduce((acc, product) => {
      return acc + Number(product.price) * product.quantity
    }, 0),
    {
      shouldShowDecimals: true,
      hasThousandsSeparator: true,
    }
  )
  return (
    <>
      <div className="sticky left-0 top-0 z-50 flex items-center justify-between px-20 pb-10 pt-32 md:px-32">
        <div className="flex flex-row gap-8">
          <Typography
            text="Cart"
            size="body-md-fixed"
            color={Color.Black}
            font="grotesk"
            weight="medium"
          />
          <Typography
            className="content-center uppercase opacity-50"
            text={
              numberOfItemsInCart > 1
                ? `(${numberOfItemsInCart} items)`
                : `(${numberOfItemsInCart} item)`
            }
            size="body-sm-fixed"
            color={Color.Charcoal}
            font="grotesk"
            weight="book"
          />
        </div>
        <TextButton
          text="Close"
          color={Color.Blue}
          style="text"
          onClick={() => cartContext.setCartPreviewOpen(false)}
          data-test-id="close-cart-preview-modal"
          uppercase={false}
          textSize="body-md-fixed"
        />
      </div>
      <div className="relative max-h-[285px] overflow-y-auto pb-16">
        <div data-test-id="cart-preview-modal-products">
          {cartItems.map((item, index) => {
            return (
              <div
                key={`cart-preview-item-${index}`}
                className={classNames("mb-0 rounded-12 bg-white", {
                  "[&:not(:last-child)]:mb-20": cartItems.length > 1,
                })}
              >
                <CartCard // TODO: update consolidate to always return image file as string
                  imageFile={
                    typeof item.image === "string"
                      ? item.image
                      : item.image.filename
                  }
                  imageAlt={
                    typeof item.image !== "string" ? item.image.alt : item.name
                  }
                  productName={item.cartTitle}
                  promotionStartDate={item.promotionStartDate}
                  promotionEndDate={item.promotionEndDate}
                  price={item.price}
                  originalPrice={item.originalPrice}
                  quantity={item.quantity}
                  maxQuantity={item.maxQuantity}
                  quantityDisabled={isLoading}
                  onQuantityUpdate={(quantity) => {
                    isConsolidatedBundle(item)
                      ? updateBundleCartQuantity(item.bundleCode, quantity)
                      : updateProductCartQuantity(item.sku, quantity)
                  }}
                  onRemove={() => {
                    if (isConsolidatedBundle(item)) {
                      removeBundle(item.bundleCode, item.storefront)
                    } else {
                      removeProduct(item.sku, item.storefront)
                    }
                    // Check if there is only 1 item in cart when removing item
                    if (cartItems.length === 1) {
                      cartContext.setCartPreviewOpen(false)
                      // If true cart closes automatically
                    }
                  }}
                  key={`${index}-${item.id}`}
                />
              </div>
            )
          })}
        </div>
      </div>
      <div className="sticky bottom-0 left-0 mx-20 flex-col bg-white md:mx-32">
        <div className="absolute h-px w-full bg-charcoal/10"></div>
        <div className="flex w-full items-end justify-between py-20">
          <div className="flex flex-row">
            <Typography
              text="Total"
              size="body-md-fixed"
              font="grotesk"
              weight="medium"
              color={Color.Black}
            />
          </div>

          <div className="flex items-baseline gap-8">
            <Typography
              className="tracking-tight"
              text={cartItems.length < 1 ? "-" : cartTotal}
              size="lead-sm-fixed"
              font="grotesk"
              weight="medium"
              color={Color.Black}
            />
          </div>
        </div>

        <div className="flex w-full flex-col items-center justify-between gap-10 pb-40">
          <PillButton
            disabled={cartItems.length === 0 || isHandlingSignupCheckout}
            text="Sign-up and checkout"
            onClick={async () => {
              await handleRedirect()

              if (isSuccessful) {
                cartContext.setCartPreviewOpen(false)
              }
            }}
            size="medium"
            data-test-id="checkout"
            style="solid"
            className="mb-16 !w-full justify-center border-[2px] text-left"
            color={Color.Blue}
          />

          <PillButton
            disabled={cartItems.length === 0 || isHandlingSignupCheckout}
            text="View my cart"
            onClick={() => {
              cartContext.setCartPreviewOpen(false)
              navigate(withPrefix(`/cart-overview`))
            }}
            size="medium"
            style="bordered"
            overrideTextColor={Color.Blue}
            className="!w-full justify-center border-[2px] text-center"
            color={Color.Blue}
          />
          <div className="inline-block md:pt-16">
            <Typography
              weight="book"
              font="grotesk"
              size="body-md"
              className="mr-4 inline"
              text="Already have an account?"
              color={Color.Black}
            />
            <TextButton
              style="text"
              weight="medium"
              uppercase={false}
              color={Color.Blue}
              textSize="body-md"
              text="Checkout here"
              onClick={() => {
                process.env.GATSBY_OAUTH_REDIRECT === "true"
                  ? handleLoginCheckout()
                  : (navigate(withPrefix("/checkout/")),
                    cartContext.setCartPreviewOpen(false))
              }}
            />
          </div>
        </div>
      </div>
    </>
  )
}

const CartPreviewModal = ({ isLoading }: Props) => {
  const [isVisible, setIsVisible] = useState(false)

  const { cartDisabled, setCartPreviewOpen, cartPreviewOpen } =
    useContext(CartContext)

  useEffect(() => {
    if (cartPreviewOpen) {
      setIsVisible(true)
    } else {
      const timer = setTimeout(() => {
        setIsVisible(false)
      }, 300) // match the duration of the cart open and close transition - duration-300

      return () => {
        clearTimeout(timer)
      }
    }
  }, [cartPreviewOpen])

  if (cartDisabled) {
    return null
  }

  return (
    <>
      <div
        data-test-id="cart-preview-modal"
        onClick={() => setCartPreviewOpen(false)}
        className={classNames(
          "fixed right-0 top-0 z-40 h-full w-full translate-x-0 transform bg-charcoal p-16 transition duration-500 ease-in-out",
          cartPreviewOpen ? `block bg-opacity-50` : `hidden bg-opacity-100`
        )}
      />
      <div
        className={classNames(
          "fixed right-0 top-48 z-50 mx-auto flex w-full max-w-[389px] transform flex-col overflow-x-hidden rounded-16 bg-white transition duration-300 ease-in-out max-[420px]:w-[95vw] sm:w-full sm:max-w-lg md:top-64 lg:top-88", // open and close animation transition duration-300 - matched in timeout
          cartPreviewOpen
            ? `translate-x-[-5.33vw] max-[420px]:translate-x-[-2.5vw] sm:translate-x-[-4.17vw] md:translate-x-[-4.69vw] lg:translate-x-[-7.22vw] xl:translate-x-[-9.76vw]`
            : `translate-x-full`
        )}
      >
        {isVisible && <CartPreviewModalContent isLoading={isLoading} />}
      </div>
    </>
  )
}

export default CartPreviewModal
